import { memo, useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { CategoryFilter, ProductFilterValue } from '@apiTypes';
import { MultiSelect, SingleSelect } from '@library';
import styles from './TireFilters.module.scss';
import { getFilterValues } from './utils/getFilterValues';
import type { FilterMultiValue, FilterSingleValue } from '.';

interface TirePairFilterCommonProps {
    filtersPossibleValues: ProductFilterValue[];
    field: string;
    placeholder: string;
    categoryFilter?: CategoryFilter;
    type: 'single' | 'multi';
}

export interface TirePairFilterSingleProps extends TirePairFilterCommonProps {
    type: 'single';
    value: string | undefined;
    onChange: (value: string | undefined) => void;
}

export interface TirePairFilterMultiProps extends TirePairFilterCommonProps {
    type: 'multi';
    value: string[];
    onChange: (value: string[]) => void;
}
type TirePairFilterProps = TirePairFilterSingleProps | TirePairFilterMultiProps;

const TirePairFilter = memo(
    ({
        field,
        filtersPossibleValues,
        value,
        onChange,
        categoryFilter,
        type,
        placeholder,
    }: TirePairFilterProps) => {
        const options = useMemo(
            () =>
                getFilterValues({
                    fitlerValues: filtersPossibleValues,
                    currentValue: value,
                    categoryFilter,
                    field,
                }),
            [categoryFilter, field, filtersPossibleValues, value]
        );

        const intl = useIntl();

        const common = {
            placeholder: intl.formatMessage({
                id: placeholder,
            }),
            id: field,
            theme: styles.selectTheme,
            options: options,
            disabled: !options.length,
            'data-test-class': `tireFilter${field}`,
            searchable: true,
            clearable: true,
        };

        if (type === 'single') {
            return (
                <SingleSelect
                    {...common}
                    value={value || ''}
                    onChange={onChange}
                />
            );
        } else {
            return (
                <MultiSelect {...common} value={value} onChange={onChange} />
            );
        }
    }
);

export interface TireFiler {
    field: string;
    placeholder: string;
}

export interface TireFiltersCommonProps {
    filtersPossibleValues: Record<string, ProductFilterValue[]>;
    filters: TireFiler[];
    categoryFilters: CategoryFilter[];
    type: 'single' | 'multi';
}

export interface TireFiltersSingleProps extends TireFiltersCommonProps {
    type: 'single';
    values: FilterSingleValue;
    onChange: (value: FilterSingleValue) => void;
}

export interface TireFiltersMultiProps extends TireFiltersCommonProps {
    type: 'multi';
    values: FilterMultiValue;
    onChange: (value: FilterMultiValue) => void;
}

type TireFiltersProps = TireFiltersSingleProps | TireFiltersMultiProps;

export const TireFilters = memo(
    ({
        filtersPossibleValues,
        values,
        filters,
        onChange,
        categoryFilters,
        type,
    }: TireFiltersProps) => {
        return (
            <>
                {filters.map(filter => {
                    const commonProps = {
                        field: filter.field,
                        filtersPossibleValues:
                            filtersPossibleValues[filter.field],
                        categoryFilter: categoryFilters.find(
                            categoryFilter =>
                                categoryFilter.field === filter.field
                        ),
                        placeholder: filter.placeholder,
                    };
                    if (type === 'single') {
                        return (
                            <TirePairFilter
                                key={filter.field}
                                type={type}
                                {...commonProps}
                                value={values[filter.field]}
                                onChange={value => {
                                    onChange({
                                        [filter.field]: value,
                                    });
                                }}
                            />
                        );
                    }
                    return (
                        <TirePairFilter
                            key={filter.field}
                            type={type}
                            {...commonProps}
                            value={values[filter.field]}
                            onChange={value => {
                                onChange({
                                    [filter.field]: value,
                                });
                            }}
                        />
                    );
                })}
            </>
        );
    }
);
